import { Suspense, lazy, useState, useEffect } from 'react';
import { animateScroll as scroll } from "react-scroll";
import { AppBar, Tabs, Tab, useTheme, IconButton } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { aboutSectionString, historySectionString, missionSectionString } from '../../constants';
import { useAppSelector } from '../../app/hooks';
import { selectLanguage } from '../../app/slices/languageSlice';
import UrgentMessageDisplay from '../../components/UrgentMessageDisplay';

const AboutSection = lazy(() => import('./AboutSection'));
const MissionSection = lazy(() => import('./MissionSection'));
const HistorySection = lazy(() => import('./HistorySection'));

const About = () => {
  const theme = useTheme();
  const language = useAppSelector(selectLanguage);
  const [showScroll, setShowScroll] = useState(false);

  const tabStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: '15px',
    fontWeight: 600,
    textTransform: 'none',
    color: theme.palette.primary.dark,
    '&:hover': {
      color: theme.palette.primary.main
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main
    }
  };

  const mainStyle = {
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.background.default,
    paddingTop: 'var(--nav-height)',
  };

  useEffect(() => {
    const checkScrollTop = () => {    
      if (!showScroll && window.pageYOffset > 0){
        setShowScroll(true)    
      } else if (showScroll && window.pageYOffset <= 0){
        setShowScroll(false)    
      }  
    };

    window.addEventListener('scroll', checkScrollTop)

    return () => window.removeEventListener('scroll', checkScrollTop)
  }, [showScroll]);

  const scrollTop = () =>{
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const scrollToSection = (id) => {
    const headerHeight = 72; // matches --nav-height in index.css (desktop value)
    let element = document.getElementById(id);
    if (element) {
      let position = element.offsetTop - headerHeight;
      scroll.scrollTo(position);
    }
  }

  return (
    <div style={ mainStyle }>
      <UrgentMessageDisplay page="About" />

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.brand.border}`,
          top: 'var(--nav-height)',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: '1'
        }}
        >
        <Tabs variant="fullWidth">
          <Tab 
            label={aboutSectionString(language)}
            onClick={() => scrollToSection('about')}
            sx={tabStyle}
          />
          <Tab 
            label={missionSectionString(language)}
            onClick={() => scrollToSection('mission')}
            sx={tabStyle}
          />
          <Tab 
            label={historySectionString(language)}
            onClick={() => scrollToSection('history')}
            sx={tabStyle}
          />
        </Tabs>

        <IconButton 
          color="primary"
          aria-label="scroll back to top"
          onClick={scrollTop}
          style={{display: showScroll ? 'flex' : 'none', marginRight: '10px'}}
        >
          <ArrowUpwardIcon />
        </IconButton>

      </AppBar>

      <Suspense fallback={<div>Loading...</div>}>
        <div id='about'><AboutSection /></div>
        <div id='mission'><MissionSection /></div>
        <div id='history'><HistorySection /></div>
      </Suspense>
    </div>
  );
};

export default About;
